import { ApolloError } from '@apollo/client';
import type { GraphQLErrorExtensions } from 'graphql';
import { describeLoanTableRefusal, type FailureDescription } from 'features/irm/core';

export const VIEW_SESSION_CLOSED: FailureDescription = {
  title: 'This view has closed',
  body: "Open the pool's loans again to keep viewing them.",
  next: 'retry',
};

export const VIEW_CHECK_FAILED_COPY = "We couldn't confirm this view just now.";

export type LoanPageError =
  | { kind: 'lock'; failure: FailureDescription }
  | { kind: 'unconfirmed' }
  | { kind: 'failed'; retryable: boolean };

// api's FORBIDDEN reasons for a page or terms request (SalePoolLoans.ts). Reopening runs every
// check again, so it is offered where it can succeed: field lists that changed with the bidder's
// level. Losing the sale or the pool cannot be undone by reopening.
const FORBIDDEN_NEXT_BY_REASON: Record<string, FailureDescription['next']> = {
  sale_access_denied: 'none',
  pool_not_readable: 'none',
  filter_field_not_allowed: 'retry',
  sort_field_not_allowed: 'retry',
  term_field_not_allowed: 'retry',
};

const KEY_SERVICE_WORDED_REASONS = new Set(['access_ended', 'reauth_required', 'unauthenticated']);

// api's errorClass values (libs/errorClassification.ts) whose answer won't change on asking again;
// saleFailure sends them with no retryable flag. Any other unmarked failure, a network failure or
// a gateway 5xx included, is worth trying again.
const UNRETRYABLE_ERROR_CLASSES = new Set(['Validation', 'Authorization', 'NotFound']);

function isRetryableFailure(extensions: GraphQLErrorExtensions | undefined): boolean {
  if (typeof extensions?.retryable === 'boolean') return extensions.retryable;
  return !UNRETRYABLE_ERROR_CLASSES.has(String(extensions?.errorClass ?? ''));
}

// What a refused page or terms request means for the open view, from api's
// createProtectedDownload-style extensions: a refusal of the bidder or of the view closes the
// view, as a heartbeat refusal does; a check that couldn't be made, or any other failure, leaves
// it open to try again. A refusal api marks retryable, or one this client doesn't know, offers a
// reopen: the open says why if it still can't go ahead.
export function loanPageError(error: ApolloError): LoanPageError {
  const apiError = error.graphQLErrors[0];
  const extensions = apiError?.extensions;
  const code = extensions?.code;
  const reason = String(extensions?.reason ?? '');
  const refusalMarkedRetryable = extensions?.retryable === true;
  const lock = (failure: FailureDescription): LoanPageError => ({
    kind: 'lock',
    failure:
      refusalMarkedRetryable && failure.next === 'none' ? { ...failure, next: 'retry' } : failure,
  });
  switch (code) {
    case 'ACCESS_REVOKED':
      return lock(describeLoanTableRefusal(reason));
    case 'PII_NOTICE_REQUIRED':
      return lock(describeLoanTableRefusal('pii_notice_required'));
    case 'FORBIDDEN':
      if (reason === 'view_session_invalid') return lock(VIEW_SESSION_CLOSED);
      if (KEY_SERVICE_WORDED_REASONS.has(reason)) return lock(describeLoanTableRefusal(reason));
      return lock({
        title: "This loan data can't be shown",
        body: apiError.message,
        next: FORBIDDEN_NEXT_BY_REASON[reason] ?? 'retry',
      });
    case 'BAD_USER_INPUT':
      return { kind: 'failed', retryable: false };
    case 'PROTECTED_DOWNLOAD_FAILED':
      if (reason === 'view_session_check_failed') return { kind: 'unconfirmed' };
      return { kind: 'failed', retryable: isRetryableFailure(extensions) };
    default:
      return { kind: 'failed', retryable: isRetryableFailure(extensions) };
  }
}
